import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Play, BookOpen, User } from 'lucide-react';
import { getDossiers, getPopularDossiers, getLatestDossiers, getFeaturedDossier, getEditions } from '../services/api';
import { formatRelativeTime } from '../utils/dateUtils';

/*
  UX LAWS APPLIED:
  - HICK'S LAW:  Reduce choices per section (max 5 hero slides, 5 top stories, 4-per-row grid, progressive disclosure via "More Stories")
  - FITTS'S LAW: All touch targets >= 48px, prominent CTAs, large click areas, reduced distance to actions
  - MILLER'S LAW: Information chunked in groups of 5±2, clear section headers, visual separators
*/

const SERIF_FONT = "'Libre Baskerville', Georgia, 'Times New Roman', serif";

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800',
  'https://images.unsplash.com/photo-1495020689067-958852a7765e?w=800',
  'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=800',
  'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=800',
  'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800',
];

const getImageUrl = (item, index = 0) => {
  if (item?.cover_image) {
    if (item.cover_image.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${item.cover_image}`;
    }
    return item.cover_image;
  }
  return PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length];
};

/* ─────────────────────────────────────────────
   TOP STORY ITEM
   Fitts: entire row is clickable (large target)
   Miller: max 5 items shown
   ───────────────────────────────────────────── */
const TopStoryItem = ({ article, index }) => (
  <Link
    to={`/dossiers/${article.dossier_id}`}
    className="flex gap-4 py-4 border-b border-[#dddddd] last:border-b-0 group min-h-[80px]"
    data-testid={`top-story-${article.dossier_id}`}
  >
    {/* Fitts: thumbnail 80x80 = large clickable area */}
    <img
      src={getImageUrl(article, index)}
      alt=""
      className="w-20 h-20 object-cover flex-shrink-0 rounded-lg"
      loading="lazy"
    />
    <div className="flex-1 min-w-0 flex flex-col justify-center">
      <h4
        className="text-[15px] font-bold text-black group-hover:text-[#e62d28] transition-colors leading-snug line-clamp-2"
        style={{ fontFamily: SERIF_FONT }}
      >
        {article.title}
      </h4>
      <p className="text-xs text-[#555555] mt-1.5">
        Por {article.author || 'Editorial'} | {formatRelativeTime(article.published_at || article.created_at)}
      </p>
    </div>
  </Link>
);

/* ─────────────────────────────────────────────
   ARTICLE CARD (4-col grid)
   Fitts: entire card is clickable
   Miller: 4 per row = manageable chunk
   ───────────────────────────────────────────── */
const ArticleCard = ({ article, index }) => (
  <Link
    to={`/dossiers/${article.dossier_id}`}
    className="group block"
    data-testid={`article-card-${article.dossier_id}`}
  >
    <div className="aspect-[4/3] mb-3 overflow-hidden bg-gray-100 rounded-lg">
      <img
        src={getImageUrl(article, index)}
        alt=""
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        loading="lazy"
      />
    </div>
    <h3
      className="text-[15px] font-bold text-black group-hover:text-[#e62d28] transition-colors leading-snug line-clamp-3 mb-2"
      style={{ fontFamily: SERIF_FONT }}
    >
      {article.title}
    </h3>
    <p className="text-xs text-[#555555]">
      Por {article.author || 'Editorial'} | {formatRelativeTime(article.published_at || article.created_at)}
    </p>
  </Link>
);

/* ─────────────────────────────────────────────
   MOBILE ARTICLE CARD
   Fitts: min-h-[72px] touch target, full row clickable
   Miller: shown in groups of 5
   ───────────────────────────────────────────── */
const MobileArticleCard = ({ article, index }) => (
  <Link
    to={`/dossiers/${article.dossier_id}`}
    className="flex gap-4 py-4 border-b border-gray-100 active:bg-gray-50 min-h-[72px]"
    data-testid={`mobile-article-${article.dossier_id}`}
  >
    {/* Fitts: 80x56 image = adequate touch target */}
    <div className="w-20 h-14 flex-shrink-0 overflow-hidden bg-gray-100 rounded-lg">
      <img src={getImageUrl(article, index)} alt="" className="w-full h-full object-cover" loading="lazy" />
    </div>
    <div className="flex-1 min-w-0 flex flex-col justify-center">
      <h3 className="text-sm font-bold text-black leading-snug line-clamp-2" style={{ fontFamily: SERIF_FONT }}>
        {article.title}
      </h3>
      <p className="text-xs text-[#555555] mt-1">
        {article.author || 'Editorial'} · {formatRelativeTime(article.published_at || article.created_at)}
      </p>
    </div>
  </Link>
);

/* ─────────────────────────────────────────────
   HERO SLIDER
   Hick: max 5 slides (reduces decision paralysis)
   Fitts: large nav arrows (44px), large CTA button (48px height)
   Miller: 1 item visible at a time (minimal cognitive load)
   ───────────────────────────────────────────── */
const HeroSlider = ({ articles }) => {
  // Hick's Law: limit to 5 slides to reduce choice overload
  const slides = articles.slice(0, 5);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused || slides.length <= 1) return;
    const timer = setInterval(() => setCurrentSlide((p) => (p + 1) % slides.length), 5000);
    return () => clearInterval(timer);
  }, [isPaused, slides.length]);

  const next = useCallback(() => setCurrentSlide((p) => (p + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setCurrentSlide((p) => (p - 1 + slides.length) % slides.length), [slides.length]);

  if (!slides.length) return null;
  const cur = slides[currentSlide];

  return (
    <div
      className="relative bg-gray-900 overflow-hidden rounded-xl h-full"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="hero-slider"
    >
      {/* Fitts: entire image area is clickable */}
      <Link to={`/dossiers/${cur.dossier_id}`} className="block relative aspect-[16/10] lg:aspect-auto lg:h-full">
        <img src={getImageUrl(cur, currentSlide)} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 lg:p-8">
          <span className="inline-block bg-[#e62d28] text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 mb-3">
            NEW
          </span>
          <h2
            className="text-lg sm:text-xl lg:text-3xl font-bold text-white leading-tight mb-4 line-clamp-2"
            style={{ fontFamily: SERIF_FONT }}
          >
            {cur.title}
          </h2>
          {/* Fitts: CTA button min-h-[48px] for easy tapping on mobile */}
          <button className="inline-flex items-center gap-2 px-6 min-h-[48px] bg-white text-gray-900 font-bold text-sm hover:bg-gray-100 transition-colors tracking-wide">
            <Play className="w-4 h-4 fill-current" />
            LEER AHORA
          </button>
        </div>
      </Link>

      {/* Fitts: navigation arrows 44x44px = comfortable touch targets */}
      {slides.length > 1 && (
        <>
          <button
            onClick={(e) => { e.preventDefault(); prev(); }}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
            aria-label="Anterior"
            data-testid="hero-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.preventDefault(); next(); }}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
            aria-label="Siguiente"
            data-testid="hero-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Hick: max 5 indicators, Fitts: each indicator has adequate tap size via padding */}
      {slides.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 flex gap-2 px-5 sm:px-6 pb-3">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.preventDefault(); setCurrentSlide(i); }}
              className={`h-1.5 rounded-full transition-all duration-300 py-2 -my-2 ${
                i === currentSlide ? 'bg-white flex-[2]' : 'bg-white/40 flex-1'
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   LATEST NEWS FEATURED (3-column asymmetric)
   Miller: exactly 3 items = easy to process
   Fitts: large image areas are clickable
   ───────────────────────────────────────────── */

/* ═══════════════════════════════════════════
   HOMEPAGE
   ═══════════════════════════════════════════ */
const HomePage = () => {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [popularDossiers, setPopularDossiers] = useState([]);
  const [latestDossiers, setLatestDossiers] = useState([]);
  const [featuredDossier, setFeaturedDossier] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [latestEdition, setLatestEdition] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dossiersData, popularData, latestData, featuredData, editionsData] = await Promise.all([
          getDossiers(), getPopularDossiers(), getLatestDossiers(), getFeaturedDossier(), getEditions('published').catch(() => [])
        ]);
        setDossiers(dossiersData || []);
        setPopularDossiers(popularData || []);
        setLatestDossiers(latestData || []);
        if (featuredData?.dossier) setFeaturedDossier(featuredData.dossier);
        const pubEditions = (editionsData || []).filter(e => e.status === 'published');
        if (pubEditions.length > 0) setLatestEdition(pubEditions[0]);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Auto-refresh every 3 minutes
    const interval = setInterval(fetchData, 180000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f8f8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-black border-t-transparent mx-auto" />
      </div>
    );
  }

  /* ALL articles sorted by newest first */
  const allSorted = [...(dossiers || [])].sort((a, b) => {
    const da = a.created_at || a.published_at || '';
    const db = b.created_at || b.published_at || '';
    return String(db).localeCompare(String(da));
  });

  /* Hero slider: featured article + highest relevance_score articles */
  const scored = [...allSorted].sort((a, b) => (b.relevance_score || 0) - (a.relevance_score || 0));
  const heroStories = featuredDossier
    ? [featuredDossier, ...scored.filter(d => d.dossier_id !== featuredDossier.dossier_id).slice(0, 4)]
    : scored.slice(0, 5);

  /* "LO MÁS RELEVANTE" sidebar: next highest scored NOT in hero */
  const heroIds = new Set(heroStories.map(d => d.dossier_id));
  const topStories = scored.filter(d => !heroIds.has(d.dossier_id)).slice(0, 5);

  /* "ÚLTIMAS NOTICIAS": ALL articles sorted by newest */
  const gridArticles = allSorted;

  /* Hick's Law: progressive disclosure — show 8 initially (2 rows of 4), load more on demand */
  const visibleGrid = showMore ? gridArticles : gridArticles.slice(0, 8);

  /* Miller's Law for mobile: show 5 items at a time */
  const mobileVisible = showMore ? gridArticles : gridArticles.slice(0, 5);

  return (
    <div className="min-h-screen bg-[#f8f8f8]" data-testid="homepage">

      {/* ══════════════════════════════════════
          SECTION 1: Hero + Top Stories
          Miller: 2 clear groups (hero = 1 focus, sidebar = 5 items)
          Hick: hero shows 1 item at a time, sidebar limited to 5
          ══════════════════════════════════════ */}
      <section className="pt-4 pb-6 sm:pt-6 sm:pb-8 lg:pt-8 lg:pb-10">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-8 lg:items-stretch">
            {/* Hero — stretches to match sidebar height */}
            <div className="lg:col-span-8">
              <HeroSlider articles={heroStories} />
            </div>

            {/* Top Stories — Miller: exactly 5 items */}
            <div className="lg:col-span-4">
              <h2 className="text-xs font-bold text-black uppercase tracking-[0.2em] pb-3 border-b-2 border-black mb-0">
                LO MÁS RELEVANTE
              </h2>
              {topStories.map((article, i) => (
                <TopStoryItem key={article.dossier_id} article={article} index={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── EDITION SEMANAL (if published) ── */}
      {latestEdition && (
        <section className="pb-10 lg:pb-14 border-t border-[#dddddd]">
          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 pt-8">
            <h2 className="text-sm font-bold text-black uppercase tracking-[0.2em] text-center mb-6 lg:mb-8">
              EDICIÓN SEMANAL
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
              {/* Cover */}
              <div className="lg:col-span-3">
                <Link to={`/edition/${latestEdition.edition_id}`} className="block">
                  <div className="relative bg-black rounded-xl overflow-hidden" style={{ aspectRatio: '3/4', maxHeight: '400px' }}>
                    {latestEdition.cover_image && (
                      <img src={latestEdition.cover_image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${latestEdition.cover_image}` : latestEdition.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />
                    <div className="absolute top-3 left-0 right-0 z-10 text-center">
                      <span className="text-xs font-bold tracking-[0.15em] text-white">LA<span className="text-cyan-400">TECHNO</span>LOGÍA</span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 z-10 p-4">
                      <h3 className="text-sm font-bold text-white leading-tight" style={{ fontFamily: SERIF_FONT }}>{latestEdition.title}</h3>
                    </div>
                  </div>
                </Link>
              </div>
              {/* Articles */}
              <div className="lg:col-span-9">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(latestEdition.sections || []).slice(0, 6).map((s, i) => {
                    const sImg = s.image ? (s.image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${s.image}` : s.image) : null;
                    return (
                      <Link key={i} to={`/edition/${latestEdition.edition_id}#article-${i}`} className="group block">
                        {sImg && (
                          <div className="aspect-[16/9] overflow-hidden rounded-lg bg-gray-100 mb-2">
                            <img src={sImg} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          </div>
                        )}
                        {s.type === 'opinion' && <span className="text-[10px] font-bold uppercase tracking-wider text-[#e62d28]">Opinión</span>}
                        <h4 className="text-[14px] font-bold text-black group-hover:text-[#e62d28] transition-colors leading-snug line-clamp-2 mt-1" style={{ fontFamily: SERIF_FONT }}>{s.title}</h4>
                        <p className="text-xs text-[#555] mt-1 flex items-center gap-1"><User className="w-3 h-3" /> {s.author}</p>
                      </Link>
                    );
                  })}
                </div>
                <Link to={`/edition/${latestEdition.edition_id}`} className="mt-5 inline-flex items-center text-sm font-bold text-black hover:text-[#e62d28] transition-colors">
                  Ver edición completa <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════
          SECTION 2: LATEST NEWS
          ══════════════════════════════════════ */}
      <section className="pb-8 sm:pb-10 lg:pb-14">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <h2 className="text-sm font-bold text-black uppercase tracking-[0.2em] text-center mb-6 sm:mb-8 lg:mb-10">
            ÚLTIMAS NOTICIAS
          </h2>

          {/* Desktop Grid: 4 per row, clean and uniform like Daily Wire */}
          <div className="hidden lg:grid grid-cols-4 gap-x-6 gap-y-10">
            {visibleGrid.map((article, i) => (
              <ArticleCard key={article.dossier_id} article={article} index={i} />
            ))}
          </div>

          {/* Mobile: stacked list */}
          <div className="lg:hidden">
            {mobileVisible.map((article, i) => (
              <MobileArticleCard key={article.dossier_id} article={article} index={i} />
            ))}
          </div>

          {/* Hick's Law: Progressive disclosure */}
          {/* Fitts: button is centered, min-h-[48px] for easy tapping */}
          {gridArticles.length > (showMore ? Infinity : 5) && !showMore && (
            <div className="text-center mt-8 sm:mt-10">
              <button
                onClick={() => setShowMore(true)}
                className="inline-flex flex-col items-center text-sm font-bold uppercase tracking-[0.15em] text-[#555555] hover:text-black transition-colors min-h-[48px] px-6 py-3"
                data-testid="more-stories-btn"
              >
                MÁS NOTICIAS
                <ChevronDown className="w-5 h-5 mt-1" />
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
